import { useCallback, useEffect, useState } from 'react';
import {
  useChildren,
  useChildActivity,
  useChildAssignments,
  useChildFlashcardDecks,
} from './useParent';

const DISMISS_KEY = 'svara_onboarding_checklist_dismissed';

export interface OnboardingItem {
  key: 'child' | 'story' | 'assignment' | 'fluency' | 'flashcard';
  label: string;
  done: boolean;
  href: string | null;
}

export interface OnboardingProgress {
  items: OnboardingItem[];
  completedCount: number;
  total: number;
  allDone: boolean;
  dismissed: boolean;
  dismiss: () => void;
}

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function useOnboardingProgress(): OnboardingProgress {
  const { data: children } = useChildren();
  const firstChild = children?.[0];
  const firstChildId = firstChild?.id ?? '';
  const enabled = !!firstChildId;

  const { data: activity } = useChildActivity(firstChildId, 20, { enabled });
  const { data: assignments } = useChildAssignments(firstChildId, false, { enabled });
  const { data: decks } = useChildFlashcardDecks(enabled ? firstChildId : '');

  const hasChild = (children?.length ?? 0) > 0;
  const hasStory = (activity ?? []).some((a) => a.type === 'reading');
  const hasAssignment = (assignments?.length ?? 0) > 0;
  const hasFluency = (activity ?? []).some((a) => a.type === 'fluency');
  const hasFlashcard = (decks?.length ?? 0) > 0;

  const items: OnboardingItem[] = [
    { key: 'child', label: 'Add a child', done: hasChild, href: null },
    {
      key: 'story',
      label: 'Generate a story for your child',
      done: hasStory,
      href: '/library/story/create',
    },
    {
      key: 'assignment',
      label: 'Create your first assignment',
      done: hasAssignment,
      href: '/parent/assignments',
    },
    {
      key: 'fluency',
      label: 'Try Read Aloud',
      done: hasFluency,
      href: firstChild ? `/parent/fluency/start?studentId=${firstChild.id}` : '/parent',
    },
    { key: 'flashcard', label: 'Try flashcards', done: hasFlashcard, href: '/flashcards' },
  ];

  const completedCount = items.filter((i) => i.done).length;
  const total = items.length;
  const allDone = completedCount === total;

  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === DISMISS_KEY) {
        setDismissed(e.newValue === '1');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore storage errors (e.g., private mode)
    }
    setDismissed(true);
  }, []);

  return {
    items,
    completedCount,
    total,
    allDone,
    dismissed,
    dismiss,
  };
}
